import * as React from 'react';
import BPromise from 'bluebird';
import { AppUser } from 'Src/views/App/App';
import { parse } from 'query-string';
import { getOrder, getSearchFilter, getSearchOrder, getServerFieldSectionWhere } from 'Src/helpers/table/table';
import { DATE_INTERVAL, FILTER_TYPE, FIRST_PAGE, LIMIT } from 'Src/consts/table';
import { Loader } from 'Src/components/Loader/Loader';
import { SelectAllCheckbox } from 'Src/components/SelectAllCheckbox/SelectAllCheckbox';
import {
  ColumnDefinition,
  getFilters2,
  getServerQueryFilter2,
  isFilterExist2,
  isSortExist,
  TABLE_SORT_DIRECTION
} from 'Src/helpers/table/table';
import { History, Location } from 'history';
import { Venue } from 'Src/models/venue';
import {
  createVenue,
  deleteVenue,
  getAllVenues,
  getVenues,
  getVenuesCount,
  updateVenue
} from 'Src/helpers/service/admin/venues';
import { VENUE_TABS } from 'Src/consts/venue';
import { GenericSummary } from 'Src/components/GenericSummary/GenericSummary';
import { Button } from 'Src/components/Button/Button';
import { VenueSummary } from 'Src/components/VenueSummary/VenueSummary';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { VenueForm } from './VenueForm/VenueForm';
import { Map } from 'Src/components/Map/Map';
import './Venues.css';
import { Pagination2 } from 'Src/components/Pagination/Pagination2';
import { loadHiddenColumns, saveHiddenColumns } from 'Src/helpers/columnVisibility/columnVisibility';

const VENUE_COLUMNS_VIEW_KEY = 'adminConsole.venues';

const COLUMNS: ColumnDefinition[] = [
  {
    text: 'Name',
    field: 'text',
    isSort: false,
    type: FILTER_TYPE.TEXT,
    accessor: ['name']
  },
  {
    text: 'Postcode',
    field: 'postcode',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    accessor: ['postcode']
  },
  {
    text: 'Type',
    field: 'isHome',
    isSort: false,
    type: FILTER_TYPE.NONE,
    cell: venue => (
      <span className={venue.isHome ? 'venuesPage__type venuesPage__type--home' : 'venuesPage__type'}>
        {venue.isHome ? 'Home' : 'Away'}
      </span>
    )
  },
  {
    text: 'Venue type',
    field: 'venueTypes',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: ['isHome']
  }
];

const getVenuesWhere = (filters: any): any => {
  const venueTypes = Array.isArray(filters.venueTypes) ? filters.venueTypes : [];
  const serverFilters = { ...filters };
  delete serverFilters.venueTypes;

  const where = getServerFieldSectionWhere(serverFilters);
  if (venueTypes.length === 1) {
    where.isHome = venueTypes[0] === 'home';
  }

  return where;
};

interface State {
  items: Venue[];
  currentPage: number;
  selectedItems: Venue[];
  sortDirection: TABLE_SORT_DIRECTION;
  sortColumnsName: string;
  isShowFilter: boolean;
  isDataFiltered: boolean;
  filters: any;
  isLoading: boolean;
  isSelectAllChecked: boolean;
  total: number;
  totalWithFilters: number;
  basePath: string;

  isDeleteVenueModalOpen: boolean;

  currentTabIndex: number;
  isEditable: boolean;
  editingVenue: Venue | null;
  isColumnsMenuOpen: boolean;
  hiddenVenueColumns: string[];
}

interface Props {
  user: AppUser;
  history: History;
  location: Location;
}

export class Venues extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      items: [],
      currentPage: FIRST_PAGE,
      selectedItems: [],
      sortDirection: '',
      sortColumnsName: '',
      isShowFilter: false,
      isDataFiltered: false,
      filters: {},
      isLoading: false,
      isSelectAllChecked: false,
      total: 0,
      totalWithFilters: 0,
      basePath: '',

      isDeleteVenueModalOpen: false,
      currentTabIndex: 0,
      isEditable: false,
      editingVenue: null,
      isColumnsMenuOpen: false,
      hiddenVenueColumns: loadHiddenColumns(props.user, VENUE_COLUMNS_VIEW_KEY)
    };
  }

  componentDidMount() {
    this.setState({
      isLoading: true
    });

    document.addEventListener('mousedown', this.onOutsideClick);
    this.setItems();
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.onOutsideClick);
  }

  onOutsideClick = (event: MouseEvent): void => {
    const target = event.target as HTMLElement;
    if (!target || !target.closest) return;
    if (
      target.closest('.venuesPage__columns') ||
      target.closest('.venuesPage__filters') ||
      target.closest('.venuesPage__filterBtn')
    ) {
      return;
    }

    if (this.state.isColumnsMenuOpen) this.setState({ isColumnsMenuOpen: false });
    if (this.state.isShowFilter) {
      this.setState({ isShowFilter: false }, () => this.setItems());
    }
  };

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.location.search !== this.props.location.search) {
      this.setState({
        isLoading: true
      });
      this.setItems(nextProps.location.search);
    }
  }

  setItems(locationSearchOverride?: string) {
    const { history, user } = this.props;

    const search = parse(typeof locationSearchOverride === 'string' ? locationSearchOverride : history.location.search);
    const page = typeof search.page !== 'undefined' ? Number(search.page) : FIRST_PAGE;

    let sortDirection: TABLE_SORT_DIRECTION = '';
    let sortColumnsName = '';

    if (typeof search.order === 'string') {
      [sortColumnsName, sortDirection] = search.order.split(':');
    }

    const sortByFieldExist = sortColumnsName !== '';
    const sortDirectionExist = sortDirection !== '';

    const filters = getFilters2(COLUMNS, search);

    const isDataFiltered = isFilterExist2(filters);

    const where = getVenuesWhere(filters);

    const order = sortByFieldExist && sortDirectionExist ? `${sortColumnsName} ${sortDirection}` : undefined;
    const serverQueryFilter = getServerQueryFilter2(page, where, order);

    const getVenuesPromise = getVenues(user, serverQueryFilter);
    const getVenuesCountPromise = getVenuesCount(user, where);
    const getVenuesTotalCountPromise = getVenuesCount(user);

    const promises = [getVenuesCountPromise, getVenuesTotalCountPromise, getVenuesPromise];

    return BPromise.all(promises)
      .then(([totalWithFiltersObj, totalObj, items]) => {
        this.setState({
          currentPage: page,
          sortDirection: sortDirection,
          sortColumnsName: sortColumnsName,
          isShowFilter: false,
          isDataFiltered: isDataFiltered,
          filters: filters,
          totalWithFilters: totalWithFiltersObj.count,
          items: items,
          total: totalObj.count,
          basePath: history.location.pathname,
          isLoading: false
        });

        return true;
      })
      .catch(error => {
        console.error(error);
        this.setState({ isLoading: false });
        return false;
      });
  }

  setCurrentPageParams = (currentPage: number): void => {
    let search: string[] = [];

    const { filters, sortColumnsName, sortDirection } = this.state;

    if (currentPage !== 1) {
      search.push(`page=${currentPage}`);
    }

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onItemClick = (index: number): void => {
    const { items } = this.state;
    const selectedItem = items[index];

    this.setState({
      editingVenue: selectedItem,
      isEditable: false
    });
  };

  onItemSelect = (index: number): void => {
    const { items, selectedItems } = this.state;
    const selectedItem = items[index];

    const selectedItemIndex = selectedItems.findIndex(item => selectedItem.id === item.id);
    let selectedItemsUpdated = [...selectedItems];

    if (selectedItemIndex !== -1) {
      selectedItemsUpdated.splice(selectedItemIndex, 1);
    } else {
      selectedItemsUpdated.push(selectedItem);
    }

    this.setState({
      selectedItems: selectedItemsUpdated
    });
  };

  onSelectAllOnPageClick = (): void => {
    const { items, selectedItems } = this.state;

    const itemsFiltered = items.filter(item => {
      return selectedItems.every(selectedItem => selectedItem.id !== item.id);
    });

    const nextSelectedItems = [...selectedItems, ...itemsFiltered];

    this.setState({
      selectedItems: nextSelectedItems,
      isSelectAllChecked: true
    });
  };

  onSelectAllClick = (): void => {
    const { selectedItems, filters } = this.state;

    this.setState({
      isLoading: true
    });

    const user = this.props.user;

    const where = getVenuesWhere(filters);

    getAllVenues(user, where).then(items => {
      const itemsFiltered = items.filter(item => {
        return selectedItems.every(selectedItem => selectedItem.id !== item.id);
      });

      const nextSelectedItems = [...selectedItems, ...itemsFiltered];

      this.setState({
        selectedItems: nextSelectedItems,
        isSelectAllChecked: true,
        isLoading: false
      });
    });
  };

  onUnselectAllClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
      isShowFilter: false,
      isColumnsMenuOpen: false
    });
  };

  onTableSortClick = (sortField: string): void => {
    const { sortDirection, sortColumnsName, filters } = this.state;

    const order = getOrder(sortField, sortDirection, sortColumnsName);

    let search: string[] = [];
    search.push(`order=${sortField}:${order}`);

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onTableFilterChange = (event, filterField: string, options?): void => {
    const filterValue = event.target.value;
    const filters = this.state.filters;
    const currentFilterField = filters[filterField];

    let nextFilters = { ...filters };

    if (typeof options !== 'undefined') {
      switch (options) {
        case DATE_INTERVAL.FROM:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              from: filterValue
            }
          };
          break;
        case DATE_INTERVAL.TO:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              to: filterValue
            }
          };
          break;
      }
    } else {
      const filter = COLUMNS.find(col => col.field === filterField);
      const filterType = filter.type;

      if (filterType === FILTER_TYPE.MULTISELECT) {
        const options = event.target.options;
        const value = [];
        for (let i = 0; i < options.length; i++) {
          if (options[i].selected) {
            value.push(options[i].value);
          }
        }
        nextFilters = {
          ...nextFilters,
          [filterField]: value
        };
      } else {
        nextFilters = {
          ...nextFilters,
          [filterField]: filterValue
        };
      }
    }

    this.setState({
      filters: nextFilters
    });
  };

  onApplyFilterClick = (): void => {
    const { filters, sortDirection, sortColumnsName } = this.state;
    let search: string[] = [];

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
      isShowFilter: false,
      isColumnsMenuOpen: false
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onClearFilterClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
      filters: getFilters2(COLUMNS, {}),
      isShowFilter: false,
      isColumnsMenuOpen: false
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: ''
    });
  };

  onClearSearchClick = (): void => {
    this.setState(
      state => ({
        filters: {
          ...state.filters,
          text: ''
        },
        selectedItems: [],
        isSelectAllChecked: false
      }),
      this.onApplyFilterClick
    );
  };

  onTableFilterClick = (event): void => {
    event.preventDefault();

    const isShowFilter = this.state.isShowFilter;

    if (isShowFilter) {
      this.setState({ isShowFilter: false }, () => this.setItems());
      return;
    }

    this.setState({
      isShowFilter: true,
      isColumnsMenuOpen: false
    });
  };

  onVenueTypeToggle = (venueType: 'home' | 'away'): void => {
    const currentTypes: string[] = Array.isArray(this.state.filters.venueTypes) ? this.state.filters.venueTypes : [];
    this.setState({
      filters: {
        ...this.state.filters,
        venueTypes: currentTypes.includes(venueType)
          ? currentTypes.filter(type => type !== venueType)
          : [...currentTypes, venueType]
      }
    });
  };

  toggleVenueColumn = (column: string): void => {
    const hiddenVenueColumns = this.state.hiddenVenueColumns.includes(column)
      ? this.state.hiddenVenueColumns.filter(item => item !== column)
      : [...this.state.hiddenVenueColumns, column];

    saveHiddenColumns(this.props.user, VENUE_COLUMNS_VIEW_KEY, hiddenVenueColumns);
    this.setState({ hiddenVenueColumns });
  };

  resetVenueColumns = (): void => {
    saveHiddenColumns(this.props.user, VENUE_COLUMNS_VIEW_KEY, []);
    this.setState({ hiddenVenueColumns: [] });
  };

  onColumnsClick = (): void => {
    const wasFilterOpen = this.state.isShowFilter;
    this.setState(
      {
        isColumnsMenuOpen: !this.state.isColumnsMenuOpen,
        isShowFilter: false
      },
      () => {
        if (wasFilterOpen) this.setItems();
      }
    );
  };

  onCreateVenueClick = (): void => {
    this.setState({
      isEditable: true,
      editingVenue: null,
      selectedItems: []
    });
  };

  deleteVenueModalOpen = (): void => {
    this.setState({
      isDeleteVenueModalOpen: true
    });
  };

  renderListActions(): React.ReactNode {
    const { selectedItems, filters, isShowFilter, isColumnsMenuOpen, hiddenVenueColumns } = this.state;
    const selectedCount = selectedItems.length;
    const venueTypes = Array.isArray(filters.venueTypes) ? filters.venueTypes : [];
    const hiddenOptionalColumns = hiddenVenueColumns.filter(column => column === 'postcode' || column === 'type');
    const activeDrawerFilters = Number(Boolean(String(filters.postcode || '').trim())) + Number(venueTypes.length > 0);
    const hasActiveFilters = Boolean(String(filters.text || '').trim()) || activeDrawerFilters > 0;

    return (
      <div className="venuesPage__listActions">
        <div className="venuesPage__toolbar">
          <label className="venuesPage__search">
            <span>Search</span>
            <input
              value={filters.text || ''}
              placeholder="Search venues…"
              onChange={event => this.onTableFilterChange(event, 'text')}
              onKeyDown={event => event.key === 'Enter' && this.onApplyFilterClick()}
            />
            {filters.text ? (
              <button type="button" aria-label="Clear search" onClick={this.onClearSearchClick}>
                ×
              </button>
            ) : null}
          </label>
          <button
            type="button"
            className={`venuesPage__filterBtn ${isShowFilter ? 'is-active' : ''}`}
            onClick={this.onTableFilterClick}
            aria-expanded={isShowFilter}
          >
            <span className="venuesPage__toolbarIcon venuesPage__toolbarIcon--filter" />
            Filters
            {activeDrawerFilters > 0 && <span className="venuesPage__actionBadge">{activeDrawerFilters}</span>}
          </button>
          <div className="venuesPage__columns">
            <button
              type="button"
              className={`venuesPage__columnsBtn ${isColumnsMenuOpen ? 'is-active' : ''}`}
              aria-expanded={isColumnsMenuOpen}
              onClick={this.onColumnsClick}
            >
              <span className="venuesPage__toolbarIcon venuesPage__toolbarIcon--columns" />
              Columns <span className="venuesPage__actionBadge">{3 - hiddenOptionalColumns.length}</span>
            </button>
            {isColumnsMenuOpen && (
              <div className="venuesPage__columnsMenu">
                <header>
                  <span>Show / hide columns</span>
                  <button type="button" onClick={this.resetVenueColumns}>
                    Reset
                  </button>
                </header>
                <label className="is-required">
                  <input type="checkbox" checked={true} disabled={true} readOnly={true} />
                  <span>Venue</span>
                  <small>Always shown</small>
                </label>
                {[
                  ['postcode', 'Postcode'],
                  ['type', 'Type']
                ].map(([key, label]) => (
                  <label key={key}>
                    <input
                      type="checkbox"
                      checked={!hiddenVenueColumns.includes(key)}
                      onChange={() => this.toggleVenueColumn(key)}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
        {hasActiveFilters && (
          <div className="venuesPage__filterChips">
            {filters.text ? (
              <span>
                <b>Search:</b> {filters.text}
                <button
                  type="button"
                  aria-label="Remove venue search"
                  onClick={() =>
                    this.setState({ filters: { ...this.state.filters, text: '' } }, this.onApplyFilterClick)
                  }
                >
                  ×
                </button>
              </span>
            ) : null}
            {filters.postcode ? (
              <span>
                <b>Postcode:</b> {filters.postcode}
                <button
                  type="button"
                  aria-label="Remove postcode filter"
                  onClick={() =>
                    this.setState({ filters: { ...this.state.filters, postcode: '' } }, this.onApplyFilterClick)
                  }
                >
                  ×
                </button>
              </span>
            ) : null}
            {venueTypes.length > 0 ? (
              <span>
                <b>Type:</b> {venueTypes.map(type => (type === 'home' ? 'Home' : 'Away')).join(', ')}
                <button
                  type="button"
                  aria-label="Remove venue type filter"
                  onClick={() =>
                    this.setState({ filters: { ...this.state.filters, venueTypes: [] } }, this.onApplyFilterClick)
                  }
                >
                  ×
                </button>
              </span>
            ) : null}
            <button type="button" className="venuesPage__filterChipsClear" onClick={this.onClearFilterClick}>
              Clear all
            </button>
          </div>
        )}
        {isShowFilter && (
          <aside
            className="venuesPage__filters"
            aria-label="Venue filters"
            onKeyDown={event => {
              if (event.key === 'Enter' && (event.target as HTMLElement).tagName !== 'BUTTON') {
                event.preventDefault();
                this.onApplyFilterClick();
              }
            }}
          >
            <header>
              <h2>Filters</h2>
              <button type="button" aria-label="Close filters" onClick={this.onTableFilterClick}>
                ×
              </button>
            </header>
            <label className="venuesPage__filterField">
              <span>Postcode</span>
              <input
                value={filters.postcode || ''}
                placeholder="Search by postcode"
                onChange={event => this.onTableFilterChange(event, 'postcode')}
              />
            </label>
            <fieldset className="venuesPage__filterGroup">
              <legend>Type</legend>
              <div>
                {[
                  ['home', 'Home'],
                  ['away', 'Away']
                ].map(([value, label]) => {
                  const isSelected = venueTypes.includes(value);
                  return (
                    <button
                      type="button"
                      key={value}
                      className={isSelected ? 'is-selected' : ''}
                      aria-pressed={isSelected}
                      onClick={() => this.onVenueTypeToggle(value as 'home' | 'away')}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </fieldset>
            <footer className="venuesPage__filtersActions">
              <button type="button" className="is-clear" onClick={this.onClearFilterClick}>
                Clear all
              </button>
              <div>
                <button type="button" onClick={this.onTableFilterClick}>
                  Cancel
                </button>
                <button type="button" className="is-apply" onClick={this.onApplyFilterClick}>
                  Apply
                </button>
              </div>
            </footer>
          </aside>
        )}
        {selectedCount > 0 && (
          <div className="venuesPage__selection">
            <span>
              {selectedCount} {selectedCount === 1 ? 'venue' : 'venues'} selected
            </span>
            <div>
              <button type="button" className="venuesPage__deleteBtn" onClick={this.deleteVenueModalOpen}>
                Delete {selectedCount === 1 ? 'venue' : 'venues'}
              </button>
              <button type="button" className="venuesPage__clearBtn" onClick={this.onUnselectAllClick}>
                Clear selection
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  renderPageHeader(): React.ReactNode {
    const { total } = this.state;

    return (
      <header className="venuesPage__pageHeader">
        <div className="venuesPage__pageTitle">
          <h1>Venues</h1>
          <span>{total}</span>
        </div>
        <button type="button" className="venuesPage__createBtn" onClick={this.onCreateVenueClick}>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 5v14M5 12h14" />
          </svg>
          Create venue
        </button>
      </header>
    );
  }
  renderVenuesTable(): React.ReactNode {
    const {
      items,
      sortDirection,
      sortColumnsName,
      selectedItems,
      currentPage,
      totalWithFilters,
      hiddenVenueColumns,
      filters
    } = this.state;
    const selectedIds = new Set(selectedItems.map(item => item.id));
    const isPageSelected = items.length > 0 && items.every(item => selectedIds.has(item.id));
    const sortIndicator = field => (sortColumnsName === field ? (sortDirection === 'ASC' ? '↑' : '↓') : '↕');
    const hiddenOptionalColumns = hiddenVenueColumns.filter(column => column === 'postcode' || column === 'type');
    const visibleColumnCount = 5 - hiddenOptionalColumns.length;
    const hasActiveFilters = isFilterExist2(filters);

    return (
      <>
        <div className="venuesPage__tableCard">
          {this.renderListActions()}
          <div className="venuesPage__tableScroll">
            <table className="venuesPage__table">
              <thead>
                <tr>
                  <th className="venuesPage__checkCell">
                    <SelectAllCheckbox
                      compact
                      isChecked={isPageSelected}
                      totalCount={totalWithFilters}
                      currentPage={currentPage}
                      pageItemsCount={items.length}
                      selectedItems={selectedItems}
                      onSelectAllOnPageClick={this.onSelectAllOnPageClick}
                      onSelectAllClick={this.onSelectAllClick}
                      onUnselectAllClick={this.onUnselectAllClick}
                    />
                  </th>
                  <th>
                    <button type="button" onClick={() => this.onTableSortClick('name')}>
                      Venue <span>{sortIndicator('name')}</span>
                    </button>
                  </th>
                  {!hiddenVenueColumns.includes('postcode') && (
                    <th>
                      <button type="button" onClick={() => this.onTableSortClick('postcode')}>
                        Postcode <span>{sortIndicator('postcode')}</span>
                      </button>
                    </th>
                  )}
                  {!hiddenVenueColumns.includes('type') && <th>Type</th>}
                  <th className="venuesPage__openCell" />
                </tr>
              </thead>
              <tbody>
                {items.map((venue, index) => (
                  <tr
                    key={venue.id}
                    className={selectedIds.has(venue.id) ? 'is-selected' : ''}
                    onClick={() => this.onItemClick(index)}
                  >
                    <td className="venuesPage__checkCell" onClick={event => event.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(venue.id)}
                        onChange={() => this.onItemSelect(index)}
                        aria-label={`Select ${venue.name}`}
                      />
                    </td>
                    <td>
                      <div className="venuesPage__venueCell">
                        <span className={venue.isHome ? 'venuesPage__pin venuesPage__pin--home' : 'venuesPage__pin'}>
                          <svg viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M12 21s7-6.5 7-12a7 7 0 1 0-14 0c0 5.5 7 12 7 12Z" />
                            <circle cx="12" cy="9" r="2.5" />
                          </svg>
                        </span>
                        <strong>{venue.name}</strong>
                      </div>
                    </td>
                    {!hiddenVenueColumns.includes('postcode') && (
                      <td className="venuesPage__postcode">{venue.postcode}</td>
                    )}
                    {!hiddenVenueColumns.includes('type') && (
                      <td>
                        <span className={venue.isHome ? 'venuesPage__type venuesPage__type--home' : 'venuesPage__type'}>
                          <i /> {venue.isHome ? 'Home' : 'Away'}
                        </span>
                      </td>
                    )}
                    <td className="venuesPage__openCell">
                      <button
                        type="button"
                        className="venuesPage__openBtn"
                        aria-label={`Open ${venue.name}`}
                        onClick={event => {
                          event.stopPropagation();
                          this.onItemClick(index);
                        }}
                      >
                        ›
                      </button>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr>
                    <td colSpan={visibleColumnCount} className="venuesPage__empty">
                      <div>
                        <span className="venuesPage__emptyIcon">
                          <svg viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M12 21s7-6.5 7-12a7 7 0 1 0-14 0c0 5.5 7 12 7 12Z" />
                            <circle cx="12" cy="9" r="2.5" />
                          </svg>
                        </span>
                        <strong>{hasActiveFilters ? 'No venues match these filters' : 'No venues yet'}</strong>
                        <p>
                          {hasActiveFilters
                            ? 'Try changing or clearing the active filters.'
                            : 'Create a venue to use it for events and fixtures.'}
                        </p>
                        <button
                          type="button"
                          className={hasActiveFilters ? 'venuesPage__emptyAction' : 'venuesPage__createBtn'}
                          onClick={hasActiveFilters ? this.onClearFilterClick : this.onCreateVenueClick}
                        >
                          {hasActiveFilters ? 'Clear filters' : 'Create venue'}
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {totalWithFilters > 0 && (
            <div className="venuesPage__pagination">
              <Pagination2
                currentPage={currentPage}
                setCurrentPageParams={this.setCurrentPageParams}
                total={totalWithFilters}
                modern={true}
              />
            </div>
          )}
        </div>
      </>
    );
  }

  isOneItemSelected() {
    const selectedItems = this.state.selectedItems;
    return selectedItems.length === 1;
  }

  getSummaryTabs(): React.ReactNode {
    const { currentTabIndex, selectedItems } = this.state;

    switch (currentTabIndex) {
      case 0:
        return <VenueSummary venue={selectedItems[0]} />;
    }
  }

  onTabClick = (event, index: number) => {
    event.preventDefault();

    this.setState({
      currentTabIndex: index
    });
  };

  onEditButtonClick = (): void => {
    this.setState({
      isEditable: true
    });
  };

  renderSummary() {
    const isOneItemSelected = this.isOneItemSelected();

    if (!isOneItemSelected) {
      return <div className="font-weight-bold">Select a venue above</div>;
    }

    const { currentTabIndex } = this.state;

    const summaryTabs = this.getSummaryTabs();
    const isShowEditButton = currentTabIndex === 0;

    return (
      <GenericSummary onTabClick={this.onTabClick} tabs={VENUE_TABS} currentTabIndex={currentTabIndex}>
        {isShowEditButton && <Button onClick={this.onEditButtonClick} text={'Edit'} customClass={'mt-3 mb-3'} />}
        {summaryTabs}
      </GenericSummary>
    );
  }

  onDeleteVenueClick = () => {
    const { selectedItems } = this.state;
    const user = this.props.user;
    const venueIdList = selectedItems.map(item => item.id);

    this.setState({
      isLoading: true
    });

    BPromise.all(venueIdList.map(venueId => deleteVenue(user, venueId))).then(res => {
      this.setState({
        isDeleteVenueModalOpen: false,
        selectedItems: [],
        editingVenue: null
      });
      this.setItems();
    });
  };

  deleteVenueModalClose = (): void => {
    this.setState({
      isDeleteVenueModalOpen: false
    });
  };

  renderDeleteVenueModal() {
    const { selectedItems, isDeleteVenueModalOpen } = this.state;
    const confirmationText = `Are you sure you want to remove ${
      selectedItems.length === 1 ? `venue ${selectedItems[0].name}` : `${selectedItems.length} venues`
    }?`;

    return (
      <SimpleModal
        isOpen={isDeleteVenueModalOpen}
        title={'Delete venue'}
        body={
          <div className="schoolStructure-warningModal schoolStructure-warningModal--danger">
            <div className="schoolStructure-warningModal__icon">!</div>
            <p>{confirmationText}</p>
          </div>
        }
        buttonText={'Delete'}
        buttonCancelText="Cancel"
        onButtonClick={this.onDeleteVenueClick}
        onCloseClick={this.deleteVenueModalClose}
        customClass="schoolStructure-modal schoolStructure-modal--warning schoolStructure-modal--danger"
        dialogStyle={{ maxWidth: 480 }}
      />
    );
  }

  onCancelButtonClick = (): void => {
    this.setState({
      isEditable: false,
      editingVenue: null
    });
  };

  renderVenueModal() {
    const { isEditable, selectedItems } = this.state;
    const { user } = this.props;

    const isCreateMode = selectedItems.length === 0;
    const title = isCreateMode ? 'Create venue' : 'Edit venue';

    return (
      <SimpleModal
        isOpen={isEditable && !this.state.editingVenue}
        title={title}
        onCloseClick={this.onCancelButtonClick}
        showFooter={false}
        customClass="modal-md"
        dialogStyle={{
          maxWidth: 680
        }}
      >
        <VenueForm
          user={user}
          venue={isCreateMode ? undefined : selectedItems[0]}
          onCancel={this.onCancelButtonClick}
          onSubmit={this.onSubmitForm}
        />
      </SimpleModal>
    );
  }

  onSubmitForm = (data): void => {
    const { user } = this.props;
    const { selectedItems, filters, editingVenue } = this.state;

    this.setState({
      isLoading: true
    });

    if (editingVenue) {
      updateVenue(user, data, editingVenue.id)
        .then(venue => {
          this.setState(
            {
              editingVenue: null,
              isEditable: false,
              selectedItems: [venue]
            },
            () => this.setItems()
          );
        })
        .catch(error => {
          console.error(error);
          this.setState({
            isLoading: false
          });
        });
      return;
    }

    if (selectedItems.length === 0) {
      createVenue(user, data)
        .then(() => {
          const where = getVenuesWhere(filters);
          return getVenuesCount(user, where);
        })
        .then(totalObj => {
          const lastPage = Math.max(1, Math.ceil(totalObj.count / LIMIT));
          const { sortDirection, sortColumnsName } = this.state;
          const search: string[] = [];

          if (lastPage !== 1) search.push(`page=${lastPage}`);
          if (isSortExist(sortDirection, sortColumnsName)) {
            search.push(getSearchOrder(sortDirection, sortColumnsName));
          }
          if (isFilterExist2(filters)) search.push(getSearchFilter(filters));

          const nextSearch = search.join('&');
          const currentSearch = this.props.history.location.search.replace(/^\?/, '');
          this.setState({ isEditable: false, selectedItems: [] }, () => {
            if (currentSearch === nextSearch) {
              this.setItems();
            } else {
              this.props.history.push({ pathname: this.state.basePath, search: nextSearch });
            }
          });
        })
        .catch(error => {
          console.error(error);
          this.setState({ isLoading: false });
        });
    } else {
      const venueId = selectedItems[0].id;

      updateVenue(user, data, venueId).then(venue => {
        this.setState({
          isEditable: false,
          selectedItems: [venue]
        });
        this.setItems();
      });
    }
  };

  renderEditDrawer(): React.ReactNode {
    const { editingVenue, isEditable } = this.state;
    const { user } = this.props;

    if (!editingVenue) {
      return null;
    }

    return (
      <>
        <div className="schoolStructure-drawerScrim" onClick={this.onCancelButtonClick} />
        <aside
          className={`schoolStructure-drawer schoolStructure-drawer--wide ${
            isEditable ? 'venuesPage__editDrawer' : ''
          }`}
          role="dialog"
          aria-modal="true"
        >
          {!isEditable && (
            <div className="schoolStructure-drawer__head">
              <div>
                <div className="schoolStructure-drawer__eyebrow">Venue</div>
                <h2>{editingVenue.name}</h2>
                <p>{editingVenue.postcode}</p>
              </div>
              <button
                type="button"
                className="schoolStructure-drawer__close"
                onClick={this.onCancelButtonClick}
                aria-label="Close"
              >
                ×
              </button>
            </div>
          )}
          <div className="schoolStructure-drawer__body">
            {isEditable ? (
              <VenueForm
                key={editingVenue.id}
                user={user}
                venue={editingVenue}
                onCancel={() => this.setState({ isEditable: false })}
                onSubmit={this.onSubmitForm}
              />
            ) : (
              <div className="venuesPage__drawerView">
                <div className="venuesPage__mapCard">
                  <Map point={editingVenue.point} customStylingClass="venuesPage__viewMap" isMarkerDraggable={false} />
                  <span className="venuesPage__postcodeChip">⌖ {editingVenue.postcode}</span>
                </div>
                <div className="venuesPage__detailRow">
                  <span>Postcode</span>
                  <strong className="venuesPage__postcode">{editingVenue.postcode}</strong>
                </div>
                <div className="venuesPage__detailRow">
                  <span>Type</span>
                  <strong
                    className={editingVenue.isHome ? 'venuesPage__type venuesPage__type--home' : 'venuesPage__type'}
                  >
                    {editingVenue.isHome ? 'Home' : 'Away'}
                  </strong>
                </div>
                <div className="venuesPage__drawerActions">
                  <button
                    type="button"
                    className="venuesPage__drawerDelete"
                    onClick={() => this.setState({ selectedItems: [editingVenue], isDeleteVenueModalOpen: true })}
                  >
                    Delete
                  </button>
                  <button type="button" className="venuesPage__drawerEdit" onClick={this.onEditButtonClick}>
                    Edit venue
                  </button>
                </div>
              </div>
            )}
          </div>
        </aside>
      </>
    );
  }

  render() {
    const { isLoading, isEditable, editingVenue } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    const classes = isEditable || editingVenue ? 'venuesScreen modal-open' : 'venuesScreen';

    return (
      <div className={`${classes} venuesPage`}>
        {/*Place for render modal windows*/}
        {this.renderDeleteVenueModal()}
        {this.renderVenueModal()}
        {this.renderEditDrawer()}
        {this.renderPageHeader()}
        <div className="venuesPage__content">{this.renderVenuesTable()}</div>
      </div>
    );
  }
}
